import {
  Alert,
  AlertDescription,
  AlertTitle,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/alert';
import { Button } from '@art-of-hospitality-solutions-llc/ui-kit/components/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/form';
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/sheet';
import { toastSuccess } from '@art-of-hospitality-solutions-llc/ui-kit/components/toast';
import { Separator } from '@art-of-hospitality-solutions-llc/ui-kit/components/separator';

import { zodResolver } from '@hookform/resolvers/zod';
import { FileTextIcon } from 'lucide-react';
import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import AssetTagInput from '@Components/AssetTagInput';
import { useUpdateAsset } from '@Hooks/useUpdateAsset';
import { getCloudinaryPdfUrl } from '@Utils/cloudinary';
import { formatDate } from '@Utils/utils';
import { AssetType, type Asset } from '@Types/assets';

import AssetPreview from './AssetPreview';

export interface AssetSheetProps {
  asset: Asset | null;
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
}

const FormSchema = z.object({
  tags: z.array(
    z.object({
      id: z.string().optional(),
      name: z.string().min(1),
    }),
  ),
});

type FormValues = z.infer<typeof FormSchema>;

export default function AssetSheet({
  asset,
  isOpen,
  setIsOpen,
}: AssetSheetProps) {
  const updateAssetMutation = useUpdateAsset();

  const currentTags = useMemo(
    () =>
      asset?.tags?.map((t) => ({
        id: t.tag?.id,
        name: t.tag?.name ?? '',
      })) ?? [],
    [asset],
  );

  const form = useForm<FormValues>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      tags: currentTags,
    },
  });

  const rootError = form.formState.errors.root?.message;

  // Sync the form to the selected asset's tags whenever the sheet opens or the
  // asset changes. Without this, react-hook-form keeps the previous asset's
  // tags because `defaultValues` is only read on initial mount.
  useEffect(() => {
    if (isOpen) {
      form.reset({ tags: currentTags });
      form.clearErrors();
    }
  }, [isOpen, currentTags, form]);

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      form.reset({ tags: currentTags });
      form.clearErrors();
    }
    setIsOpen(open);
  };

  const onSubmit = async (values: FormValues) => {
    if (!asset) return;

    form.clearErrors('root');

    try {
      await updateAssetMutation.mutateAsync({
        id: asset.id,
        payload: { tags: values.tags },
      });

      toastSuccess('Asset updated successfully.');
      setIsOpen(false);
    } catch (error: any) {
      console.error('Error updating asset:', error);
      form.setError('root', {
        type: 'server',
        message: 'Unable to update this asset. Please try again.',
      });
    }
  };

  return (
    <Sheet open={isOpen} onOpenChange={handleOpenChange}>
      <SheetContent onPointerDownOutside={(e) => e.preventDefault()}>
        <SheetHeader>
          <SheetTitle>Manage Asset</SheetTitle>
        </SheetHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="flex h-full flex-col"
          >
            <SheetBody className="flex-1 space-y-6 overflow-y-auto">
              {rootError && (
                <Alert variant="destructive">
                  <AlertTitle>Something went wrong</AlertTitle>
                  <AlertDescription>{rootError}</AlertDescription>
                </Alert>
              )}

              {asset && (
                <>
                  {/* Preview */}
                  <div className="flex h-64 items-center justify-center overflow-hidden rounded-md border bg-zinc-100">
                    <AssetPreview
                      key={asset.id}
                      asset={asset}
                      className="max-h-64 max-w-full object-contain"
                    />
                  </div>

                  {asset.type === AssetType.DOCUMENT && (
                    <a
                      href={getCloudinaryPdfUrl(asset.cloudinaryId)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 text-sm text-zinc-700 hover:underline"
                    >
                      <FileTextIcon className="h-4 w-4 shrink-0" />
                      Open PDF in a new tab
                    </a>
                  )}

                  {/* Metadata */}
                  <div className="space-y-1 text-sm text-zinc-500">
                    <div>
                      <span className="font-medium text-zinc-700">Type:</span>{' '}
                      {asset.type}
                    </div>
                    <div>
                      <span className="font-medium text-zinc-700">
                        Uploaded:
                      </span>{' '}
                      {formatDate(asset.createdAt)}
                    </div>
                  </div>

                  <Separator />

                  {/* Tags */}
                  <FormField
                    control={form.control}
                    name="tags"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Tags</FormLabel>
                        <FormControl>
                          <AssetTagInput
                            value={field.value}
                            onChange={field.onChange}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </>
              )}
            </SheetBody>

            <SheetFooter>
              <Button
                type="button"
                variant="link"
                onClick={() => handleOpenChange(false)}
                disabled={updateAssetMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                loading={updateAssetMutation.isPending}
                disabled={
                  updateAssetMutation.isPending || !form.formState.isDirty
                }
              >
                Save
              </Button>
            </SheetFooter>
          </form>
        </Form>
      </SheetContent>
    </Sheet>
  );
}
